import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/common/Button";
import { useAppI18n } from "@/hooks/useAppI18n";

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  confirmVariant?: "destructive" | "default";
  isLoading?: boolean;
  confirmButtonProps?: Record<string, any>;
}

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmVariant = "default",
  isLoading = false,
  confirmButtonProps,
}: ConfirmDialogProps) {
  const { t } = useAppI18n();
  const resolvedConfirmLabel = confirmLabel ?? t("confirm");
  const handleClose = useCallback(() => {
    if (!isLoading) onClose();
  }, [onClose, isLoading]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isLoading, handleClose]);

  if (!open) return null;

  const confirmButtonClass =
    confirmVariant === "destructive"
      ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground font-rubik"
      : "bg-sunbird-theme-accent hover:bg-sunbird-theme-accent/90 text-white font-rubik";

      return createPortal(
        <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-sunbird-gray-f3"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape") handleClose();
        }}
        role="presentation"
      >
        <h2 className="text-xl font-semibold font-rubik text-sunbird-obsidian mb-2">
          {title}
        </h2>
        <p className="text-sm text-muted-foreground mb-6 font-rubik">
          {description}
        </p>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={isLoading}
            className="font-rubik border-sunbird-gray-d0 text-sunbird-obsidian hover:bg-sunbird-gray-f3 hover:text-sunbird-obsidian"
            data-edataid="confirm-dialog-cancel"
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onConfirm}
            disabled={isLoading}
            className={confirmButtonClass}
            data-edataid="confirm-dialog-confirm"
            {...confirmButtonProps}
          >
            {isLoading ? t("confirmDialog.pleaseWait") : resolvedConfirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
